import { useCallback, useMemo, useState } from "react";

/**
 * Neatly packages up the props and calculations necessary for modals to have
 * item pagination. For pagination useful for search result pages, see
 * `usePaginatedSearch`.
 */
export const usePaginate = <T>() => {
  const [items, setItemsInternal] = useState<T[]>([]);
  const [index, setIndex] = useState(0);

  const isEmpty = items.length === 0;

  const setItems = useCallback(
    (updatedItems: T[]) => {
      if (updatedItems.length === 0 && isEmpty) {
        //
        // special case: if both are empty, don't change state
        // this avoids rerenders for callers who are doing something like
        // setItems(myArray ?? [])
        //
        return;
      }
      setItemsInternal(updatedItems);
    },
    [isEmpty],
  );

  return useMemo(() => {
    const next = () => {
      setIndex(Math.min(items.length, index + 1));
    };
    const prev = () => {
      setIndex(Math.max(0, index - 1));
    };

    return {
      setItems,
      setIndex,
      next,
      prev,
      index,
      results: items,
      item: items.at(index),
      // Modal props
      props: {
        progress: {
          current: index + 1,
          total: items?.length ?? 0,
        },
        showPagination: true,
        isNextDisabled: !(index < (items?.length ?? 0) - 1),
        isPreviousDisabled: index === 0,
        onNext: next,
        onPrevious: prev,
      },
    };
  }, [setItems, index, items]);
};
